import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { FADE_UP_FAST } from "@/lib/motionPresets";
import { Check, Loader2, AlertCircle, FileText } from "lucide-react";
import { api } from "@/lib/apiClient";
import { log } from "@/lib/log";

const GOLD = "#C9A84C";
const inputCls = "w-full bg-transparent border border-white/20 focus:border-[#C9A84C] outline-none px-4 py-3 text-white placeholder-white/30 text-sm transition-colors";

const Field = ({ label, children, optional }) => (
  <label className="block">
    <span className="block text-[11px] uppercase tracking-[0.2em] mb-2" style={{ color: GOLD }}>
      {label}{optional && <span className="text-white/35 normal-case tracking-normal ml-2">(optionnel)</span>}
    </span>
    {children}
  </label>
);

export default function RapportQuestionnaire() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [order, setOrder] = useState(null);
  const [state, setState] = useState(token ? "loading" : "invalid");
  const [form, setForm] = useState({ prenoms: "", nom: "", nom_jeune_fille: "", birthdate: "", question: "", contexte: "" });
  const [error, setError] = useState("");

  useEffect(() => {
    document.title = "Questionnaire — Analyse de Précision Triple Fusion™ | Académie AETHERIS";
    if (!token) return;
    api.get(`/rapport/order/${encodeURIComponent(token)}`)
      .then(({ data }) => { setOrder(data); setState(data.status === "questionnaire_received" ? "already" : "form"); })
      .catch((e) => { log.warn("[rapport] commande introuvable :", e); setState("invalid"); });
  }, [token]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.prenoms.trim() || !form.nom.trim() || !form.birthdate || !form.question.trim()) {
      setError("Merci de renseigner vos prénoms, votre nom, votre date de naissance et votre question.");
      return;
    }
    setError("");
    setState("sending");
    try {
      const [y, m, d] = form.birthdate.split("-");
      await api.post("/rapport/questionnaire", { token, ...form, birthdate: `${d}/${m}/${y}` });
      setState("done");
    } catch (err) {
      log.error("[rapport] envoi questionnaire échoué :", err);
      setError("Une erreur est survenue. Merci de réessayer dans un instant.");
      setState("form");
    }
  };

  return (
    <div className="min-h-screen bg-brand-ink text-white pt-28 pb-24" data-testid="rapport-questionnaire-page">
      <div className="max-w-[760px] mx-auto px-6 md:px-10">
        <motion.div {...FADE_UP_FAST}>
          <div className="inline-flex items-center gap-2 mb-6" style={{ color: GOLD }}>
            <FileText size={18} strokeWidth={1.5} />
            <p className="text-xs uppercase tracking-[0.25em]">Analyse de Précision Triple Fusion™</p>
          </div>

          {state === "loading" && <Loader2 className="animate-spin" style={{ color: GOLD }} data-testid="questionnaire-loading" />}

          {state === "invalid" && (
            <div data-testid="questionnaire-invalid">
              <AlertCircle className="mb-6" size={32} strokeWidth={1.5} style={{ color: GOLD }} />
              <h1 className="font-serif text-3xl md:text-4xl mb-4">Ce lien n'est pas valide.</h1>
              <p className="text-white/60 leading-relaxed">Utilisez le lien reçu par email après votre commande. En cas de difficulté, écrivez à{" "}
                <a href="mailto:gestion.svh@serenite-essence.fr" className="underline" style={{ color: GOLD }}>gestion.svh@serenite-essence.fr</a>.</p>
            </div>
          )}

          {(state === "already" || state === "done") && (
            <div className="border p-10 text-center" style={{ borderColor: `${GOLD}66` }} data-testid="questionnaire-done">
              <Check className="mx-auto mb-5" size={32} style={{ color: GOLD }} />
              <h1 className="font-serif text-3xl md:text-4xl mb-4">Votre questionnaire est bien reçu.</h1>
              <p className="text-white/60 leading-relaxed">Rodrigue CASSILDÉ prend maintenant le temps de lire votre situation. Votre Analyse vous parviendra en PDF sous 48 h à l'adresse de votre commande.</p>
              <Link to="/journal" className="inline-block mt-8 text-xs uppercase tracking-[0.2em] underline underline-offset-4" style={{ color: GOLD }}>Lire le Journal Souverain</Link>
            </div>
          )}

          {(state === "form" || state === "sending") && (
            <>
              <h1 className="font-serif text-4xl sm:text-5xl leading-tight mb-6">Quelques questions pour personnaliser votre Analyse</h1>
              <p className="text-white/60 leading-relaxed mb-10">
                {order?.name ? `${order.name.split(" ")[0]}, merci` : "Merci"} pour votre commande. Ces informations servent exclusivement à la rédaction de votre lecture Triple Fusion™.
              </p>
              <form onSubmit={onSubmit} className="space-y-7" data-testid="questionnaire-form" noValidate>
                <div className="grid md:grid-cols-2 gap-6">
                  <Field label="Tous vos prénoms (ordre de l'état civil)">
                    <input className={inputCls} value={form.prenoms} onChange={set("prenoms")} placeholder="Ex : Marie Claire Sophie" data-testid="q-prenoms" />
                  </Field>
                  <Field label="Nom de famille">
                    <input className={inputCls} value={form.nom} onChange={set("nom")} data-testid="q-nom" />
                  </Field>
                  <Field label="Nom de jeune fille" optional>
                    <input className={`${inputCls} border-dashed`} value={form.nom_jeune_fille} onChange={set("nom_jeune_fille")} data-testid="q-njf" />
                  </Field>
                  <Field label="Date de naissance">
                    <input className={`${inputCls} [color-scheme:dark]`} type="date" value={form.birthdate} onChange={set("birthdate")} data-testid="q-birthdate" />
                  </Field>
                </div>
                <Field label="La question professionnelle que vous souhaitez éclairer">
                  <textarea className={`${inputCls} min-h-[120px]`} value={form.question} onChange={set("question")} placeholder="Formulez-la comme vous la posez vous-même, en une ou deux phrases." data-testid="q-question" />
                </Field>
                <Field label="Contexte utile" optional>
                  <textarea className={`${inputCls} min-h-[100px]`} value={form.contexte} onChange={set("contexte")} placeholder="Secteur, taille d'équipe, échéance, ce qui a déjà été tenté…" data-testid="q-contexte" />
                </Field>
                {error && <p className="text-sm text-red-300" data-testid="questionnaire-error">{error}</p>}
                <button type="submit" disabled={state === "sending"} data-testid="questionnaire-submit"
                  className="inline-flex items-center justify-center gap-3 px-10 py-5 text-xs uppercase tracking-[0.2em] font-semibold text-brand-ink transition-transform hover:scale-[1.02] disabled:opacity-60"
                  style={{ backgroundColor: GOLD }}>
                  {state === "sending" && <Loader2 size={16} className="animate-spin" />} Envoyer mon questionnaire
                </button>
              </form>
            </>
          )}
        </motion.div>
      </div>
    </div>
  );
}
